import { useState, useEffect, useCallback } from 'react';

const STORAGE_PREFIX = 'tablePrefs:';

export function useTablePrefs(pageKey, defaults = {}) {
  const storageKey = STORAGE_PREFIX + pageKey;
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch (e) {}
    return { columnOrder: [], hiddenColumns: [], sorting: null, columnWidths: {}, filters: {}, ...defaults };
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(prefs)); } catch (e) {}
  }, [storageKey, prefs]);

  const setColumnOrder = useCallback(order => setPrefs(p => ({ ...p, columnOrder: order })), []);
  const setHiddenColumns = useCallback(hidden => setPrefs(p => ({ ...p, hiddenColumns: hidden })), []);
  const setSorting = useCallback(sorting => setPrefs(p => ({ ...p, sorting })), []);
  const setColumnWidth = useCallback((colId, width) => setPrefs(p => ({
    ...p,
    columnWidths: { ...(p.columnWidths || {}), [colId]: Math.max(40, Math.round(width)) },
  })), []);
  const resetColumnWidths = useCallback(() => setPrefs(p => ({ ...p, columnWidths: {} })), []);
  const setFilter = useCallback((colId, value) => setPrefs(p => {
    const next = { ...(p.filters || {}) };
    if (value == null || value === '') delete next[colId];
    else next[colId] = value;
    return { ...p, filters: next };
  }), []);
  const clearFilters = useCallback(() => setPrefs(p => ({ ...p, filters: {} })), []);
  const reset = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch (e) {}
    setPrefs({ columnOrder: [], hiddenColumns: [], sorting: null, columnWidths: {}, filters: {}, ...defaults });
  }, [storageKey, defaults]);

  return { prefs, setColumnOrder, setHiddenColumns, setSorting, setColumnWidth, resetColumnWidths, setFilter, clearFilters, reset };
}
